import {
  AssessmentScope,
  AssessmentScopeIntegration,
  GroupAssessmentScope,
  ItemAssessmentScope,
} from "@p0security/shared/types/assessment";
import { List } from "antd";
import { GraphTooltip } from "components/GraphTooltip";
import { SpaceBetweenDiv } from "components/divs";
import { capitalize, every, groupBy } from "lodash";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";

import { targetLogo } from "./TargetLogo";

export const scopeLabel: Record<
  AssessmentScope["integration"],
  Partial<Record<AssessmentScope["type"], string>>
> = {
  aws: {
    project: "account",
  },
  azure: {
    project: "subscription",
  },
  custom: {
    project: "system",
  },
  gcloud: {
    group: "folder",
    project: "project",
  },
  k8s: {
    project: "cluster",
  },
  "local-agents": {
    project: "scope",
  },
  okta: {
    project: "directory",
  },
  workspace: {
    project: "organization",
  },
  "azure-ad": {
    project: "tenant",
  },
  "copilot-studio": {
    project: "environment",
  },
  "entra-id": {
    project: "tenant",
  },
  ssh: {
    project: "environment",
  },
};

const isItemScopes = (
  scopes: AssessmentScope[]
): scopes is ItemAssessmentScope[] =>
  every(scopes, (s) => s.type === "project");

const isGroupScopes = (
  scopes: AssessmentScope[]
): scopes is GroupAssessmentScope[] => every(scopes, (s) => s.type === "group");

const renderScopes = (ids: string[]) =>
  ids.length > 5 ? (
    <GraphTooltip title={ids.join(", ")}>
      {`${ids.at(0)} and ${ids.length - 1} more`}
    </GraphTooltip>
  ) : (
    ids.join(", ")
  );

export const TargetsList: React.FC<{
  targets: AssessmentScope[];
  actionItem?: React.ReactElement;
}> = ({ targets, actionItem }) => {
  const getTargetLogo = useCallback(
    (integration: AssessmentScopeIntegration) => targetLogo(integration),
    []
  );

  const grouped = useMemo(
    () => Object.entries(groupBy(targets, "integration")),
    [targets]
  ) as [AssessmentScopeIntegration, AssessmentScope[]][];

  const renderTargetItem = useCallback(
    ([integration, items]: [AssessmentScopeIntegration, AssessmentScope[]]) => (
      <List.Item>
        <SpaceBetweenDiv style={{ width: "100%" }}>
          <List.Item.Meta
            avatar={getTargetLogo(integration)}
            title={pluralize(
              capitalize(
                scopeLabel[integration]?.[items[0].type] ?? items[0].type
              ),
              items.length
            )}
            description={
              isItemScopes(items)
                ? renderScopes(items.map((i) => i.id))
                : isGroupScopes(items)
                  ? renderScopes(items.map((i) => i.label ?? i.value))
                  : "(entire organization)"
            }
          />
          {actionItem && <div>{actionItem}</div>}
        </SpaceBetweenDiv>
      </List.Item>
    ),
    [actionItem, getTargetLogo]
  );

  return <List dataSource={grouped} renderItem={renderTargetItem} />;
};
